/* global axios */
/**
 * What Meta already holds about the business (spec P-8.1): the WhatsApp Business Profile of each
 * connected number, and the account's own name. Read from CHATWOOT, like `api/templates.js` and
 * `api/flows.js`: the profile sits behind the channel's own Graph token, which only Chatwoot holds,
 * so the fork's read-only seam (`deploy/branding/profile/`) asks Meta and this module asks it.
 *
 * NEVER A THROW. "Write it for me" works from whatever it finds: a number whose profile Meta would
 * not give is left out, and an account name it could not read is `''`. The screen lists what was
 * found, so nothing is ever drafted from a source the owner did not see.
 *
 * `specs/route-contract.spec.js` drives every method here against the routes Rails draws.
 */
import { templatesApi } from './templates.js';

const accountBase = accountId =>
  `/api/v1/accounts/${encodeURIComponent(String(accountId))}`;

const text = value => (typeof value === 'string' ? value : '');

/** One number's profile, in the shape the first draft sends, or null. */
export const readProfile = (inbox, data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return {
    inboxId: inbox.id,
    name: text(inbox.name),
    phoneNumber: text(inbox.phoneNumber),
    about: text(data.about),
    description: text(data.description),
    address: text(data.address),
    email: text(data.email),
    vertical: text(data.vertical),
    websites: Array.isArray(data.websites)
      ? data.websites.filter(site => typeof site === 'string').slice(0, 5)
      : [],
  };
};

export const businessProfileApi = {
  /** One WhatsApp number's profile, or null when Meta gave none. */
  async profile(accountId, inbox, signal) {
    try {
      const { data } = await axios.get(
        `${accountBase(accountId)}/waslo/inboxes/${encodeURIComponent(String(inbox.id))}/business_profile`,
        { signal }
      );
      return readProfile(inbox, data);
    } catch {
      return null;
    }
  },

  /** Every connected WhatsApp number's profile that Meta gave, five numbers at most. */
  async profiles(accountId, signal) {
    let senders = [];
    try {
      senders = await templatesApi.senders(accountId, signal);
    } catch {
      return [];
    }
    const read = await Promise.all(
      senders
        .slice(0, 5)
        .map(inbox => businessProfileApi.profile(accountId, inbox, signal))
    );
    return read.filter(Boolean);
  },

  /** The account's own name, or `''`. */
  async accountName(accountId, signal) {
    try {
      const { data } = await axios.get(accountBase(accountId), { signal });
      return text(data?.name);
    } catch {
      return '';
    }
  },
};

export default businessProfileApi;
